import { Trans } from "@lingui/macro";
import IconRefresh from "@mui/icons-material/Refresh";
import {
  UiAlert,
  UiButton,
  UiDialogResponsive,
  UiLink,
  UiTypography,
} from "@smp/ui";
import { REACT_APP__TIMEOUT_VERSION_AS_FORCE } from "@smp/utils";
import { useEffect, useMemo } from "react";
import { useIdle } from "react-use";

import { timeoutToShowError } from "./dialog_page_reload.static";
import { IProps } from "./dialog_page_reload.types";

export const DialogPageReload = ({ skip, versionTarget }: IProps) => {
  const pageReload = () => {
    window.location.reload();
  };

  const isIdle = useIdle(timeoutToShowError);

  const isIdleToForce = useIdle(REACT_APP__TIMEOUT_VERSION_AS_FORCE);

  useEffect(() => {
    isIdleToForce && pageReload();
  }, [isIdleToForce]);

  return useMemo(
    () => (
      <UiDialogResponsive
        actionsRight={[
          <UiButton onClick={skip}>
            <Trans>Continue working session</Trans>
          </UiButton>,
          <UiButton
            color="primary"
            onClick={pageReload}
            startIcon={<IconRefresh />}
            variant="contained"
          >
            <Trans>Refresh the page</Trans>
          </UiButton>,
        ]}
        open={true}
      >
        <UiTypography gutterBottom gutterBottomSize="xl" variant="h3">
          <Trans>New version available {versionTarget}</Trans>
        </UiTypography>
        <UiTypography gutterBottom gutterBottomSize="md">
          <Trans>
            In order to continue your work, we recommend updating to the newest
            version. In most cases, you can do that by simply{" "}
            <UiLink onClick={pageReload}>refreshing the page</UiLink>.
          </Trans>
        </UiTypography>
        {!isIdle && (
          <UiAlert gutterBottom variant="info">
            <UiTypography>
              <Trans>
                If you continue to see this message after refreshing the page,
                try clearing cache. Read more on how to do this{" "}
                <UiLink
                  href={`https://faq.${window.location.hostname}/ru/articles/3772126-%D0%BE%D1%87%D0%B8%D1%81%D1%82%D0%BA%D0%B0-%D0%BA%D1%8D%D1%88%D0%B0`}
                  target="_blank"
                >
                  in our article
                </UiLink>
                .
              </Trans>
            </UiTypography>
          </UiAlert>
        )}
        {!isIdle && (
          <UiAlert gutterBottom variant="warning">
            <Trans>
              You can continue using the outdated version of the application.
              Unfortunately, then we will not be able to guarantee stable
              performance.
            </Trans>
          </UiAlert>
        )}
        {isIdle && (
          <UiAlert gutterBottom variant="error">
            <Trans>
              The page will reload automatically if you do not take any action
              within {REACT_APP__TIMEOUT_VERSION_AS_FORCE - timeoutToShowError}{" "}
              sec.
            </Trans>
          </UiAlert>
        )}
      </UiDialogResponsive>
    ),
    [isIdle, skip, versionTarget],
  );
};
